import { createContext, useContext, useState, type ReactNode } from "react";
import { Language } from "../types";
import translations from "../locales";

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
    undefined
);

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error("useLanguage must be used within LanguageProvider");
    }
    return context;
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
    const [language, setLanguage] = useState<Language>(() => {
        if (typeof window !== "undefined") {
            const saved = localStorage.getItem("language") as Language;
            if (saved && ["ko", "en", "ja", "zh"].includes(saved)) {
                return saved;
            }
            const browserLang = navigator.language.split("-")[0];
            if (["ko", "en", "ja", "zh"].includes(browserLang)) {
                return browserLang as Language;
            }
        }
        return "ko";
    });

    const t = (key: string): string => {
        return translations[language][key] || translations.en[key] || key;
    };

    const handleSetLanguage = (lang: Language) => {
        setLanguage(lang);
        localStorage.setItem("language", lang);
    };

    return (
        <LanguageContext.Provider
            value={{ language, setLanguage: handleSetLanguage, t }}
        >
            {children}
        </LanguageContext.Provider>
    );
};
